import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileCheck, 
  Eye, 
  Send, 
  RotateCcw, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  ShieldCheck, 
  BookOpen, 
  Info, 
  ClipboardList, 
  Users, 
  Check, 
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { ExaminationPassModal } from '../../components/admissions/EnglishAdmissionsDocuments';
import './AssessmentCurriculumManager.css';

export const OFFICIAL_ASSESSMENT_DEFAULTS = {
  preschool: {
    level_code: 'preschool',
    level_name: 'Preschool (Playgroup)',
    assessment_title: 'School Readiness & Developmental Observation',
    assessment_subjects: 'Pengenalan Dasar (Bentuk/Warna/Angka Sederhana), Koordinasi Motorik Halus & Kasar, Kemandirian, Interaksi Sosial & Wawancara Singkat Orang Tua',
    assessment_description: 'Calon peserta didik mengikuti sesi observasi bermain interaktif bersama guru observer di ruang aktivitas anak selama 45 menit, dilanjutkan dengan wawancara singkat orang tua/wali mengenai riwayat tumbuh kembang dan kebiasaan anak.',
    assessment_preparation: "1. Kartu Tanda Peserta Seleksi & Observasi (wajib dicetak fisik).\n2. Pakaian ganti cadangan (1 stel lengkap) dan botol minum/snack anak.\n3. Alat mewarnai sederhana (krayon 12 warna).\n4. Buku catatan kesehatan / fotokopi buku KIA (jika ada).",
    assessment_rules: "1. Peserta wajib hadir di kampus minimal 15 menit sebelum sesi observasi dimulai.\n2. Mengenakan pakaian bebas, rapi, bersih, sopan, dan nyaman untuk bergerak aktif (dilengkapi sepatu/kaus kaki).\n3. Kartu Tanda Peserta ini wajib ditunjukkan kepada petugas registrasi saat kedatangan.\n4. Demi kenyamanan anak, maksimal 2 orang tua/wali pendamping diperkenankan berada di area sekolah.\n5. Orang tua/pendamping menunggu di area ruang tunggu yang telah disediakan selama anak berada di ruang observasi.",
    parent_instructions: 'Area drop-off dan ruang tunggu orang tua ber-AC tersedia di lantai 1 dekat resepsionis. Mohon pastikan anak sudah sarapan dan dalam kondisi sehat/bugar sebelum sesi observasi.',
  },
  kindergarten: {
    level_code: 'kindergarten',
    level_name: 'Kindergarten (TK A & TK B)',
    assessment_title: 'Early Childhood Readiness & Social-Emotional Observation',
    assessment_subjects: 'Literasi Awal (Pengenalan Huruf & Bunyi), Numerasi Konseptual (Berhitung 1-20), Keterampilan Motorik, Fokus Konsentrasi & Wawancara Kesiapan Sekolah Bersama Orang Tua',
    assessment_description: 'Sesi observasi kesiapan belajar berlangsung selama 60 menit dalam kelompok kecil, meliputi aktivitas motorik halus, bercerita singkat, logika sederhana, serta wawancara ramah orang tua dengan tim psikolog/konselor sekolah.',
    assessment_preparation: "1. Kartu Tanda Peserta Seleksi (dicetak fisik).\n2. Alat tulis dasar (pensil 2B/pensil segitiga, penghapus, dan krayon/pensil warna).\n3. Botol minum anak berlabel nama.\n4. Dokumen asli (Akta Kelahiran & Kartu Keluarga) untuk pencocokan data.",
    assessment_rules: "1. Peserta wajib hadir di kampus Sekolah Permata Harapan minimal 15 menit sebelum waktu dimulai.\n2. Mengenakan pakaian seragam sekolah asal (bila dari PG) atau pakaian bebas berkerah yang rapi dan bersepatu tertutup.\n3. Kartu Peserta ditunjukkan kepada pengawas ruang saat presensi kehadiran.\n4. Pendamping tidak diperkenankan memasuki ruang observasi selama tes berlangsung agar anak dapat diobservasi secara mandiri.\n5. Orang tua wajib siap di ruang tunggu saat sesi wawancara orang tua dipanggil oleh panitia.",
    parent_instructions: 'Disediakan area bermain dan ruang tunggu ramah anak di dekat lobi utama. Panitia akan memanggil orang tua ke ruang wawancara secara bergantian.',
  },
  primary: {
    level_code: 'primary',
    level_name: 'Primary School (SD)',
    assessment_title: 'Primary Academic Foundation & Holistic Assessment',
    assessment_subjects: 'Literasi Bahasa Indonesia & Basic English Reading, Logika Matematika Dasar, Sains Pemula & Wawancara Kesiapan Akademik Siswa-Orang Tua',
    assessment_description: 'Tes seleksi masuk tertulis offline dan pemetaan kesiapan belajar berlangsung selama 75 menit meliputi kemampuan literasi dasar, logika matematika, serta wawancara terpadu dengan Koordinator Akademik Primary.',
    assessment_preparation: "1. Kartu Tanda Peserta Seleksi Masuk (telah dicetak).\n2. Alat tulis lengkap: Pensil 2B, penghapus pensil, rautan berpenampung, dan penggaris 15 cm.\n3. Papan ujian / clipboard kayu transparan (opsional).\n4. Khusus siswa pindahan (Grade 2-6): Fotokopi rapor sekolah asal 2 semester terakhir.",
    assessment_rules: "1. Peserta wajib hadir di lokasi kampus minimal 20 menit sebelum waktu ujian dimulai.\n2. Mengenakan seragam sekolah asal atau pakaian kemeja berkerah rapi, celana/rok sopan panjang, dan bersepatu sekolah.\n3. Menunjukkan Kartu Peserta kepada Panitia PPDB di meja registrasi lantai dasar.\n4. Tidak diperkenankan membawa gawai/handphone atau alat bantu elektronik ke dalam ruang ujian.\n5. Orang tua / wali pendamping wajib menunggu dengan tertib di area lobi/kantin yang telah dialokasikan.",
    parent_instructions: 'Parkir kendaraan roda empat diarahkan ke area parkir tamu. Ruang tunggu orang tua dilengkapi akses Wi-Fi sekolah.',
  },
  secondary: {
    level_code: 'secondary',
    level_name: 'Secondary School (SMP)',
    assessment_title: 'Cambridge Lower Secondary Entrance Assessment & Academic Interview',
    assessment_subjects: 'English Language Proficiency (Reading Comprehension & Grammar), Mathematics Logic & Problem Solving, Basic Integrated Science & Student-Family Interview',
    assessment_description: 'Asesmen diagnostik terstandar selama 90 menit untuk mengukur kesiapan kurikulum internasional Cambridge, meliputi literasi bahasa Inggris, pemecahan masalah matematika dan sains dasar, diikuti 15 menit wawancara langsung siswa dan orang tua dengan tim pimpinan akademik.',
    assessment_preparation: "1. Kartu Tanda Peserta Ujian Masuk (Official Examination Pass dicetak).\n2. Alat tulis ujian: Pensil 2B, pulpen hitam/biru, penghapus, dan penggaris 30 cm.\n3. Kartu pelajar dari sekolah dasar (SD) asal atau kartu identitas siswa.\n4. Fotokopi lembar nilai rapor SD kelas 5 dan kelas 6 semester ganjil.",
    assessment_rules: "1. Peserta wajib hadir di kampus Sekolah Permata Harapan paling lambat 15 menit sebelum bel masuk berbunyi.\n2. Mengenakan seragam sekolah asal (putih-merah / batik sekolah asal) lengkap dan bersepatu hitam bertali/formal.\n3. Seluruh ponsel pintar (smartphone), jam pintar (smartwatch), dan kalkulator wajib dinonaktifkan dan diletakkan di tas sebelum memasuki ruang tes.\n4. Peserta yang terlambat lebih dari 15 menit tidak diberikan tambahan waktu ujian.\n5. Kartu Tanda Peserta diletakkan di sudut kanan atas meja ujian selama tes berlangsung.",
    parent_instructions: 'Wawancara keluarga (Family Interview) merupakan bagian integral dari proses penerimaan. Setidaknya satu orang tua (ayah/ibu/wali resmi) wajib mendampingi dan hadir pada sesi wawancara tatap muka.',
  },
  sma: {
    level_code: 'sma',
    level_name: 'Senior High School (SMA)',
    assessment_title: 'Cambridge Upper Secondary & Senior Academic Placement Examination',
    assessment_subjects: 'Advanced English Literacy, Mathematics Reasoning, Science / Social Studies Stream Diagnostics & Leadership-Academic Interview',
    assessment_description: 'Asesmen seleksi dan pemetaan peminatan jurusan selama 120 menit berstandar IGCSE/Kurikulum Nasional meliputi literasi kritis Bahasa Inggris, penalaran matematika tingkat lanjut, sains/sosial terpadu, serta wawancara komitmen akademik dan minat karier siswa bersama Kepala Sekolah.',
    assessment_preparation: "1. Kartu Tanda Peserta Ujian Masuk (telah dicetak pada kertas A4).\n2. Alat tulis lengkap (pensil 2B, pulpen gel hitam, penghapus, tipex, dan mistar).\n3. Fotokopi rapor SMP semester 1 sampai semester 5 yang telah dilegalisir.\n4. Portofolio prestasi akademik/non-akademik, sertifikat kejuaraan, atau sertifikat tes bahasa Inggris (jika ada).",
    assessment_rules: "1. Peserta hadir di lokasi kampus sekurang-kurangnya 20 menit sebelum asesmen dimulai untuk proses verifikasi data.\n2. Wajib mengenakan seragam SMP asal rapi, beridentitas lengkap, dan bersepatu tertutup pantofel/sneakers rapi.\n3. Dilarang keras membawa alat komunikasi elektronik, kalkulator grafik, smartwatch, dan catatan ke meja asesmen.\n4. Segala bentuk kecurangan atau komunikasi antar-peserta selama ujian berakibat diskualifikasi langsung.\n5. Peserta wajib menjaga integritas dan ketertiban lingkungan akademik selama berada di area sekolah.",
    parent_instructions: 'Sesi wawancara pimpinan sekolah bersama calon siswa dan orang tua akan dilaksanakan di ruang konsultasi akademik lantai 2 sesuai urutan nomor registrasi peserta.',
  },
};

export default function AssessmentCurriculumManager({ apiBase, onConfigUpdated }) {
  const [levels, setLevels] = useState([]);
  const [selectedLevelCode, setSelectedLevelCode] = useState('secondary');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    assessment_title: '',
    assessment_subjects: '',
    assessment_description: '',
    assessment_preparation: '',
    assessment_rules: '',
    parent_instructions: '',
  });

  const loadLevels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const json = await res.json();
        const raw = json?.data || [];
        const lvlList = Array.isArray(raw) ? raw : (raw.levels || []);
        setLevels(lvlList);
      }
    } catch (err) {
      console.warn('[AssessmentCurriculumManager] Fetch warning:', err);
      setFeedback({ type: 'error', text: 'Gagal memuat data jenjang pendidikan dari server' });
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadLevels();
  }, [loadLevels]);

  const currentLevel = levels.find((l) => l.level_code === selectedLevelCode) || levels[0] || null;

  // Sync formData when selectedLevel changes
  useEffect(() => {
    if (currentLevel) {
      // Check if there is pending payload
      const pending = currentLevel.pending_assessment_payload;
      const initialSource = pending || currentLevel;
      const defaultVals = OFFICIAL_ASSESSMENT_DEFAULTS[currentLevel.level_code] || {};

      setFormData({
        assessment_title: initialSource.assessment_title || defaultVals.assessment_title || '',
        assessment_subjects: initialSource.assessment_subjects || defaultVals.assessment_subjects || '',
        assessment_description: initialSource.assessment_description || defaultVals.assessment_description || '',
        assessment_preparation: initialSource.assessment_preparation || defaultVals.assessment_preparation || '',
        assessment_rules: initialSource.assessment_rules || defaultVals.assessment_rules || '',
        parent_instructions: initialSource.parent_instructions || defaultVals.parent_instructions || '',
      });
    }
  }, [currentLevel]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetToDefault = () => {
    if (!currentLevel) return;
    const defaults = OFFICIAL_ASSESSMENT_DEFAULTS[currentLevel.level_code];
    if (!defaults) return;
    if (window.confirm(`Muat template standar resmi untuk jenjang ${currentLevel.level_name}? Teks formulir saat ini akan digantikan dengan template resmi.`)) {
      setFormData({
        assessment_title: defaults.assessment_title,
        assessment_subjects: defaults.assessment_subjects,
        assessment_description: defaults.assessment_description,
        assessment_preparation: defaults.assessment_preparation,
        assessment_rules: defaults.assessment_rules,
        parent_instructions: defaults.parent_instructions,
      });
      setFeedback({ type: 'info', text: `Template standar ${currentLevel.level_name} berhasil dimuat. Klik "Ajukan Perubahan ke Kepala Sekolah" untuk mengirimkan pengajuan.` });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentLevel) return;

    if (!formData.assessment_title.trim() || !formData.assessment_subjects.trim()) {
      setFeedback({ type: 'error', text: 'Judul Asesmen dan Fokus Materi wajib diisi' });
      return;
    }

    setSaving(true);
    setFeedback(null);

    try {
      const res = await fetch(`${apiBase}/admin/admissions/assessment-configs/${currentLevel.level_code}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const json = await res.json();
      if (res.ok && json.ok) {
        setFeedback({
          type: 'success',
          text: json.message || `Perubahan materi asesmen untuk ${currentLevel.level_name} berhasil diajukan ke Kepala Sekolah (Principal).`,
        });
        await loadLevels();
        if (onConfigUpdated) onConfigUpdated();
      } else {
        setFeedback({
          type: 'error',
          text: json.message || 'Gagal mengajukan perubahan ke Kepala Sekolah',
        });
      }
    } catch (err) {
      setFeedback({ type: 'error', text: `Terjadi kesalahan jaringan: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Mock app object for live PDF test card preview
  const sampleTestCardApp = {
    id: 9999,
    registration_no: 'REG-PHS-2026-TEST',
    child_full_name: 'Jonathan Wijaya (Contoh Calon Siswa)',
    level_name: currentLevel?.level_name || 'Secondary School / SMP',
    period_name: 'Gelombang 1 (Early Bird)',
    academic_year: '2026/2027',
    birth_place: 'Jakarta',
    birth_date: '2014-05-18',
    gender: 'male',
    previous_school: 'SD Bintang Kejora Jakarta',
    father_name: 'Hendrawan Wijaya',
    parent_phone: '0812-3456-7890',
    test_schedule: {
      slot_name: 'Sesi Tes & Observasi Masuk Batch 1',
      test_date: '2026-12-12',
      start_time: '09:00',
      end_time: '11:00',
      campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13, Jakarta Barat',
      room_name: 'Ruang Asesmen Terpadu 102',
      assessment_title: formData.assessment_title,
      assessment_subjects: formData.assessment_subjects,
      assessment_preparation: formData.assessment_preparation,
      assessment_rules: formData.assessment_rules,
      parent_instructions: formData.parent_instructions,
    },
  };

  const isPendingPrincipal = currentLevel?.assessment_approval_status === 'pending_principal' || Boolean(currentLevel?.pending_assessment_payload);
  const isRejected = currentLevel?.assessment_approval_status === 'rejected';

  return (
    <div className="assessment-mgr-container">
      {/* Header Banner */}
      <div className="assessment-mgr-header">
        <div className="assessment-mgr-title-box">
          <div className="assessment-mgr-icon-badge">
            <FileCheck size={26} color="#ffffff" />
          </div>
          <div>
            <div className="assessment-mgr-title-row">
              <h2>Kurikulum Materi Asesmen &amp; Ketentuan Kartu Tes</h2>
              <span className="assessment-mgr-role-tag">
                <ShieldCheck size={13} />
                <span>Persetujuan: Kepala Sekolah (Principal Approval)</span>
              </span>
            </div>
            <p>
              Kelola judul asesmen, fokus materi, perlengkapan yang wajib dibawa, tata tertib, dan instruksi orang tua per jenjang pendidikan. Seluruh perubahan akan otomatis dicetak pada <strong>Kartu Tanda Peserta Seleksi &amp; Observasi</strong> setelah disetujui Kepala Sekolah.
            </p>
          </div>
        </div>
      </div>

      {/* Feedback Toast / Alert */}
      {feedback && (
        <div className={`assessment-alert ${feedback.type}`}>
          {feedback.type === 'success' && <CheckCircle2 size={18} />}
          {feedback.type === 'error' && <AlertCircle size={18} />}
          {feedback.type === 'info' && <Info size={18} />}
          <div className="assessment-alert-content">
            <span>{feedback.text}</span>
          </div>
          <button type="button" className="assessment-alert-dismiss" onClick={() => setFeedback(null)}>
            &times;
          </button>
        </div>
      )}

      {/* Level Selector Tabs */}
      <div className="assessment-level-tabs">
        {(levels.length > 0 ? levels : Object.values(OFFICIAL_ASSESSMENT_DEFAULTS)).map((lvl) => {
          const isSelected = lvl.level_code === selectedLevelCode;
          const isLvlPending = lvl.assessment_approval_status === 'pending_principal' || Boolean(lvl.pending_assessment_payload);
          const isLvlRejected = lvl.assessment_approval_status === 'rejected';

          return (
            <button
              key={lvl.level_code}
              type="button"
              className={`assessment-level-tab-btn ${isSelected ? 'active' : ''}`}
              onClick={() => setSelectedLevelCode(lvl.level_code)}
            >
              <span className="lvl-tab-name">{lvl.level_name}</span>
              {isLvlPending && (
                <span className="lvl-status-dot pending" title="Menunggu persetujuan Principal">
                  Pending
                </span>
              )}
              {isLvlRejected && (
                <span className="lvl-status-dot rejected" title="Pengajuan ditolak Principal">
                  Revisi
                </span>
              )}
              {!isLvlPending && !isLvlRejected && (
                <span className="lvl-status-dot approved" title="Resmi Aktif">
                  Aktif
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Status Alert for Selected Level */}
      {isPendingPrincipal && (
        <div className="assessment-status-box pending">
          <Clock size={20} className="status-box-icon" />
          <div className="status-box-text">
            <strong>Pengajuan Perubahan Materi Asesmen Sedang Ditinjau Kepala Sekolah</strong>
            <p>
              Perubahan untuk jenjang <strong>{currentLevel?.level_name}</strong> telah diajukan dan sedang menunggu persetujuan Principal. Anda dapat memperbarui draf di bawah atau mengajukan revisi kembali.
            </p>
            {currentLevel?.pending_assessment_payload?.submitted_at && (
              <span className="status-box-sub">
                Diajukan pada: {new Date(currentLevel.pending_assessment_payload.submitted_at).toLocaleString('id-ID')}
              </span>
            )}
          </div>
        </div>
      )}

      {isRejected && (
        <div className="assessment-status-box rejected">
          <AlertCircle size={20} className="status-box-icon" />
          <div className="status-box-text">
            <strong>Pengajuan Sebelumnya Memerlukan Revisi dari Kepala Sekolah</strong>
            <p>
              Catatan Peninjauan: <em>"{currentLevel?.assessment_review_note || 'Mohon sesuaikan ketentuan materi dan perlengkapan peserta.'}"</em>
            </p>
            <span className="status-box-sub">
              Ditinjau oleh: {currentLevel?.assessment_reviewed_by || 'Kepala Sekolah'} &bull; Silakan sesuaikan form di bawah dan ajukan kembali.
            </span>
          </div>
        </div>
      )}

      {/* Form Container */}
      <form onSubmit={handleSubmit} className="assessment-editor-card">
        <div className="assessment-card-header">
          <div>
            <h3>Konfigurasi Kartu Tes Jenjang: {currentLevel?.level_name}</h3>
            <p className="card-sub-p">
              Data berikut dicetak otomatis pada Kartu Tanda Peserta seleksi resmi yang diunduh orang tua.
            </p>
          </div>
          <div className="card-header-actions">
            <button
              type="button"
              className="btn-outline-secondary"
              onClick={handleResetToDefault}
              title="Kembalikan nilai ke template standar resmi"
            >
              <RotateCcw size={14} />
              <span>Muat Template Standar</span>
            </button>
            <button
              type="button"
              className="btn-preview-card"
              onClick={() => setPreviewModalOpen(true)}
              title="Lihat tampilan Kartu Tanda Peserta Tes langsung dengan data saat ini"
            >
              <Eye size={15} />
              <span>Pratinjau Kartu Peserta Tes (PDF)</span>
            </button>
          </div>
        </div>

        <div className="assessment-form-body">
          {/* Field 1: Judul / Format Asesmen */}
          <div className="form-group-item">
            <label className="assessment-label">
              <span>Judul Tahap Asesmen / Format Seleksi</span>
              <span className="req-star">*</span>
            </label>
            <input
              type="text"
              className="assessment-input"
              value={formData.assessment_title}
              onChange={(e) => handleInputChange('assessment_title', e.target.value)}
              placeholder="Contoh: School Readiness & Developmental Observation"
              required
            />
            <span className="assessment-hint">
              Ditampilkan sebagai header format seleksi pada Kartu Tanda Peserta (misal: Cambridge Entrance Assessment).
            </span>
          </div>

          {/* Field 2: Fokus Materi Asesmen */}
          <div className="form-group-item">
            <label className="assessment-label">
              <span>Fokus Materi Asesmen &amp; Aspek yang Diteskan</span>
              <span className="req-star">*</span>
            </label>
            <textarea
              rows={2}
              className="assessment-textarea"
              value={formData.assessment_subjects}
              onChange={(e) => handleInputChange('assessment_subjects', e.target.value)}
              placeholder="Contoh: English Language Proficiency, Mathematics Logic & Problem Solving, Basic Integrated Science & Student-Family Interview"
              required
            />
            <span className="assessment-hint">
              Ditampilkan pada kotak "Fokus Materi Asesmen" kartu tes agar orang tua dan calon siswa memahami materi yang diujikan.
            </span>
          </div>

          {/* Field 3: Deskripsi Lengkap Alur Tes */}
          <div className="form-group-item">
            <label className="assessment-label">
              <span>Deskripsi Lengkap Alur Observasi &amp; Ujian untuk Orang Tua</span>
            </label>
            <textarea
              rows={3}
              className="assessment-textarea"
              value={formData.assessment_description}
              onChange={(e) => handleInputChange('assessment_description', e.target.value)}
              placeholder="Jelaskan durasi, alur observasi anak, dan teknis wawancara orang tua..."
            />
            <span className="assessment-hint">
              Deskripsi komprehensif alur pelaksanaan tes di kampus untuk panduan staf dan orang tua.
            </span>
          </div>

          {/* Field 4: Perlengkapan Wajib yang Harus Dibawa */}
          <div className="form-group-item">
            <label className="assessment-label">
              <span>Perlengkapan yang Wajib Dibawa Saat Tes Offline</span>
            </label>
            <textarea
              rows={4}
              className="assessment-textarea font-mono"
              value={formData.assessment_preparation}
              onChange={(e) => handleInputChange('assessment_preparation', e.target.value)}
              placeholder="1. Kartu Tanda Peserta Seleksi (dicetak fisik)&#10;2. Alat tulis lengkap (pensil 2B, penghapus, penggaris)&#10;3. Fotokopi dokumen persyaratan&#10;4. Botol minum anak"
            />
            <span className="assessment-hint">
              <strong>Tips:</strong> Tuliskan 1 perlengkapan per baris. Sistem akan otomatis memformatnya menjadi daftar checklist rapi pada Kartu Tanda Peserta.
            </span>
          </div>

          {/* Field 5: Tata Tertib & Ketentuan Peserta Tes */}
          <div className="form-group-item">
            <label className="assessment-label">
              <span>Tata Tertib &amp; Ketentuan Peserta Tes Offline</span>
            </label>
            <textarea
              rows={5}
              className="assessment-textarea font-mono"
              value={formData.assessment_rules}
              onChange={(e) => handleInputChange('assessment_rules', e.target.value)}
              placeholder="1. Peserta wajib hadir di kampus minimal 15 menit sebelum ujian dimulai.&#10;2. Mengenakan seragam sekolah asal atau pakaian rapi berkerah dan bersepatu.&#10;3. Kartu Tanda Peserta wajib ditunjukkan kepada petugas registrasi saat kedatangan.&#10;4. Dilarang membawa alat komunikasi elektronik ke dalam ruang ujian.&#10;5. Orang tua wajib menunggu di area ruang tunggu yang telah disediakan."
            />
            <span className="assessment-hint">
              <strong>Tips:</strong> Tuliskan 1 aturan per baris. Sistem akan menyajikannya sebagai poin tata tertib bernomor 1, 2, 3 pada kartu ujian cetak.
            </span>
          </div>

          {/* Field 6: Catatan Khusus Ruang Tunggu Orang Tua */}
          <div className="form-group-item">
            <label className="assessment-label">
              <span>Catatan Khusus Orang Tua / Pendamping &amp; Ruang Tunggu</span>
            </label>
            <textarea
              rows={2}
              className="assessment-textarea"
              value={formData.parent_instructions}
              onChange={(e) => handleInputChange('parent_instructions', e.target.value)}
              placeholder="Contoh: Area drop-off dan ruang tunggu orang tua tersedia di Lobi Lantai 1 dekat resepsionis. Orang tua wajib siap saat dipanggil untuk sesi wawancara keluarga."
            />
            <span className="assessment-hint">
              Ditampilkan pada kotak sorotan khusus di bagian bawah Kartu Tanda Peserta untuk kenyamanan penjemputan &amp; ruang tunggu.
            </span>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="assessment-card-footer">
          <div className="footer-left-info">
            <ShieldCheck size={16} color="#059669" />
            <span>Perubahan yang Anda ajukan akan diverifikasi Kepala Sekolah sebelum aktif di kartu tes siswa.</span>
          </div>
          <div className="footer-right-btns">
            <button
              type="button"
              className="btn-preview-card secondary"
              onClick={() => setPreviewModalOpen(true)}
            >
              <Eye size={15} />
              <span>Pratinjau Tampilan</span>
            </button>
            <button
              type="submit"
              className="btn-submit-curriculum"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-dot" />
                  <span>Mengirimkan Pengajuan...</span>
                </>
              ) : (
                <>
                  <Send size={15} />
                  <span>Ajukan Perubahan ke Kepala Sekolah</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Live Preview Modal (Official ExaminationPassModal) */}
      {previewModalOpen && (
        <ExaminationPassModal
          app={sampleTestCardApp}
          onClose={() => setPreviewModalOpen(false)}
        />
      )}
    </div>
  );
}
